import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ModalShell from '../../shared/components/modal/ModalShell';
import ModalFormFooter from '../../shared/components/modal/ModalFormFooter';
import FormField from '../../shared/components/modal/FormField';
import SearchableSelect from '../../shared/components/table/SearchableSelect';
import PasswordInput from '../../shared/components/PasswordInput';
import useOrganisationStore from '../../store/organisations-store';

const INITIAL_FORM = {
  name: '',
  email: '',
  password: '',
  organisation_id: '',
  is_admin: false,
};

const CreateUserModal = ({ isOpen, onRequestClose, onCreate }) => {
  const searchOrganisations = useOrganisationStore((s) => s.searchOrganisations);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [orgLabel, setOrgLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidPassword, setIsValidPassword] = useState(false);

  const loadOrgOptions = useCallback(
    (search) => searchOrganisations({ search }),
    [searchOrganisations]
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setOrgLabel('');
  };

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  const isSubmitDisabled =
    !formData.name.trim() ||
    !formData.organisation_id.trim() ||
    !formData.email.trim() ||
    !formData.password.trim() ||
    !isValidPassword;

  const handleCreate = async () => {
    setIsLoading(true);
    try {
      await onCreate(formData);
      resetForm();
      toast.success('User Created Successfully');
      onRequestClose();
    } catch (error) {
      console.error('Failed to create user or set password:', error);
      toast.error('Failed to create user or set password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onRequestClose();
  };

  const footer = (
    <ModalFormFooter
      onCancel={handleCancel}
      onSubmit={handleCreate}
      isLoading={isLoading}
      isDisabled={isSubmitDisabled}
      submitLabel="Create"
      loadingLabel="Creating..."
    />
  );

  return (
    <ModalShell isOpen={isOpen} onClose={handleCancel} title="Create User" footer={footer} maxWidthClass="max-w-xl">
      <form onSubmit={(e) => e.preventDefault()}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            field={{ name: 'name', label: 'Name', type: 'text', required: true }}
            value={formData.name}
            onChange={handleChange}
          />
          <div className="flex flex-col">
            <label className="mb-2 block text-sm font-bold text-gray-700">Organisation</label>
            <SearchableSelect
              name="organisation_id"
              value={formData.organisation_id}
              selectedLabel={orgLabel}
              onChange={(id, option) => {
                setFormData((prev) => ({ ...prev, organisation_id: id || '' }));
                setOrgLabel(option?.name || '');
              }}
              loadOptions={loadOrgOptions}
              placeholder="Select Organisation"
              className="w-full"
              required
            />
          </div>
          <FormField
            field={{ name: 'email', label: 'Email', type: 'email', required: true }}
            value={formData.email}
            onChange={handleChange}
          />
          <div className="mb-2 flex flex-col">
            <label className="mb-2 block text-sm font-bold text-gray-700">Password</label>
            <PasswordInput
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password"
              setIsValidPassword={setIsValidPassword}
            />
          </div>
          <FormField
            field={{ name: 'is_admin', label: 'Is Admin', type: 'checkbox' }}
            value={formData.is_admin}
            onChange={handleChange}
          />
        </div>
      </form>
    </ModalShell>
  );
};

export default CreateUserModal;
